import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { Loader } from "components/Loader";
import { tooltipStyleProps } from "services/constants/Styles";
import { companyHeader } from "../AdministrationHeaders";
import {
  companyUi,
  paginationFooter,
} from "../adminListScreenUi";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  statusChip,
} from "services/constants/designTokens";
import { ApproveTable } from "pages/TeamZone/Approve/approveUi";
import { useSelector } from "react-redux";
import { scrollTop } from "services/constants";
import moment from "moment";
import { useSavedHeaderViews } from "components/CustomComponents/useSavedHeaderViews";
import { SavedViewSelector } from "components/CustomComponents/SavedViewSelector";

const COMPANY_LIST_DEFAULT_VIEW_ID = "__default_company_list_view__";
const COMPANY_LIST_DEFAULT_HEADER_KEYS = companyHeader.map(
  (header) => header.key,
);

export const ListView = ({
  isLoading,
  masterDataForSearch,
  allCompanies,
  page,
  setPage,
  onRowClick,
  selectedCompanyId,
  statusFilter,
  subModuleId,
  // Filtering and downloads use viewer access; company status changes
  // require approver access.
  viewAccess,
  hasApproverAccess,
  anchorEl,
  setAnchorEl,
  editDetails,
  changeCompanyType,
  activatedAndDeactivateCompany,
}) => {
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // null (not []) until CustomFilter's own effect computes a real filtered
  // set — visibleCompanies below falls back to the raw allCompanies rows
  // while this is null, so the table shows real data the instant the fetch
  // completes instead of flashing "No Matching Results" for a render.
  const [filteredCompanies, setFilteredCompanies] = useState(null);
  const tableContainerRef = useRef(null);
  // CustomFilter's rendered height isn't fixed — its chip row only appears
  // once a filter/sort is applied — so the table header sticking below it
  // needs this measured, not guessed, to know how far down to offset itself.
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  // Callback ref (not a plain ref) — this component swaps between a Loader
  // and the real content based on `isLoading`, so the DOM node backing
  // customFilterNode unmounts/remounts across that transition. A plain ref
  // + a mount-once effect would measure the very first node and then go
  // stale forever once the Loader swap tears it down; this re-fires (and
  // the effect below re-attaches its observer) every time the node changes.
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const footerRef = useCallback((node) => setFooterNode(node), []);

  useEffect(() => {
    if (!customFilterNode) return;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);

  useEffect(() => {
    if (!footerNode) return;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  const { topbarCompanyName, domain, userDetails } = useSelector(
    (state) => state.tokenReducer,
  );

  // Additional filter applied on top of whatever CustomFilter itself
  // produced, driven by the "Total/Active/Inactive Companies" stat cards.
  // Only reuse filter results computed from the current company/search scope.
  const filteredDataReady = filteredCompanies?.source === allCompanies;
  const companiesBase = filteredDataReady ? filteredCompanies.rows : allCompanies;
  const visibleCompanies = useMemo(() => {
    if (!statusFilter) return companiesBase;
    const wantActive = statusFilter === "active";
    return companiesBase.filter(
      (company) => (company?.status === "Active") === wantActive,
    );
  }, [companiesBase, statusFilter]);

  useEffect(() => {
    scrollTop(tableContainerRef);
  }, [page]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setFilteredCompanies({ source: allCompanies, rows: filteredData });
      if (page * rowsPerPage >= filteredData.length) {
        setPage(0);
      }
    },
    [allCompanies, page, rowsPerPage, setPage],
  );

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [topbarCompanyName],
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, [setPage]);

  const {
    savedViewsForScope,
    activeViewId,
    selectView,
    selectDefaultView,
    deleteView,
    createView,
    upsertView,
    updateView,
  } = useSavedHeaderViews({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "company_saved_views",
    onViewChange: handleSavedViewChange,
    scope: "default",
    defaultViewId: COMPANY_LIST_DEFAULT_VIEW_ID,
  });

  const persistedDefaultViewId = activeViewId || COMPANY_LIST_DEFAULT_VIEW_ID;
  const [currentViewId, setCurrentViewId] = useState(null);

  useEffect(() => {
    setCurrentViewId((prev) => {
      if (prev && prev !== COMPANY_LIST_DEFAULT_VIEW_ID) {
        return prev;
      }
      return persistedDefaultViewId;
    });
  }, [persistedDefaultViewId]);

  const baseDefaultHeaders = useMemo(() => {
    const keys = new Set(COMPANY_LIST_DEFAULT_HEADER_KEYS);
    return companyHeader.filter((header) => keys.has(header.key));
  }, []);

  const storedDefaultView = useMemo(
    () =>
      savedViewsForScope.find(
        (view) => view.id === COMPANY_LIST_DEFAULT_VIEW_ID,
      ) || null,
    [savedViewsForScope],
  );

  const customSavedViews = useMemo(
    () =>
      savedViewsForScope.filter(
        (view) => view.id !== COMPANY_LIST_DEFAULT_VIEW_ID,
      ),
    [savedViewsForScope],
  );

  const defaultView = useMemo(
    () =>
      (storedDefaultView?.isCustomDefault ? storedDefaultView : null) || {
        id: COMPANY_LIST_DEFAULT_VIEW_ID,
        name: "Default View",
        headerConfig: baseDefaultHeaders.map((header) => ({
          key: header.key,
          name: header.name,
        })),
        headerKeys: baseDefaultHeaders.map((header) => header.key),
        isDefaultView: true,
      },
    [baseDefaultHeaders, storedDefaultView],
  );

  const effectiveViewId = currentViewId ?? persistedDefaultViewId;

  const activeView = useMemo(() => {
    if (effectiveViewId === COMPANY_LIST_DEFAULT_VIEW_ID) {
      return defaultView;
    }

    return (
      customSavedViews.find((view) => view.id === effectiveViewId) ||
      defaultView
    );
  }, [customSavedViews, defaultView, effectiveViewId]);

  const visibleHeaders = useMemo(() => {
    const headerByKey = new Map(companyHeader.map((header) => [header.key, header]));

    if (activeView?.headerConfig?.length) {
      const orderedHeaders = activeView.headerConfig
        .map((entry) => {
          const baseHeader = headerByKey.get(entry.key);
          if (!baseHeader) return null;
          return {
            ...baseHeader,
            name: entry.name?.trim() || baseHeader.name,
          };
        })
        .filter(Boolean);

      return orderedHeaders.length ? orderedHeaders : baseDefaultHeaders;
    }

    if (activeView?.headerKeys?.length) {
      const selectedKeys = new Set(activeView.headerKeys);
      const filteredHeaders = companyHeader.filter((header) =>
        selectedKeys.has(header.key),
      );
      return filteredHeaders.length ? filteredHeaders : baseDefaultHeaders;
    }

    return baseDefaultHeaders;
  }, [activeView, baseDefaultHeaders]);

  const handleSelectView = useCallback(
    (viewId) => {
      setCurrentViewId(viewId);
      handleSavedViewChange();
    },
    [handleSavedViewChange],
  );

  const handleSelectDefaultView = useCallback(() => {
    setCurrentViewId(COMPANY_LIST_DEFAULT_VIEW_ID);
    handleSavedViewChange();
  }, [handleSavedViewChange]);

  const handleMakeDefaultView = useCallback(
    (viewId) => {
      if (viewId === COMPANY_LIST_DEFAULT_VIEW_ID) {
        selectDefaultView();
        setCurrentViewId(COMPANY_LIST_DEFAULT_VIEW_ID);
        return;
      }

      selectView(viewId);
      setCurrentViewId(viewId);
    },
    [selectDefaultView, selectView],
  );

  const handleCreateView = useCallback(
    (viewName, headerConfig) => {
      createView(viewName, headerConfig, { activate: false });
    },
    [createView],
  );

  const handleDeleteView = useCallback(
    (viewId) => {
      if (currentViewId === viewId) {
        setCurrentViewId(COMPANY_LIST_DEFAULT_VIEW_ID);
      }
      deleteView(viewId);
    },
    [currentViewId, deleteView],
  );

  const handleEditDefaultView = useCallback(
    (viewId, viewName, headerConfig) => {
      upsertView(
        viewId || COMPANY_LIST_DEFAULT_VIEW_ID,
        viewName || "Default View",
        headerConfig,
        "Default view updated successfully.",
        { isCustomDefault: true },
      );
      setCurrentViewId(COMPANY_LIST_DEFAULT_VIEW_ID);
    },
    [upsertView],
  );

  const titleOverride = useMemo(
    () => (
      <SavedViewSelector
        headers={companyHeader}
        defaultHeaders={baseDefaultHeaders}
        defaultView={defaultView}
        savedViews={customSavedViews}
        activeView={activeView}
        activeViewId={effectiveViewId}
        persistedDefaultViewId={persistedDefaultViewId}
        screenAccess={viewAccess}
        onSelectDefaultView={handleSelectDefaultView}
        onSelectView={handleSelectView}
        onCreateView={handleCreateView}
        onUpdateView={(viewId, viewName, headerConfig) => {
          if (viewId === COMPANY_LIST_DEFAULT_VIEW_ID) {
            handleEditDefaultView(viewId, viewName, headerConfig);
            return;
          }
          updateView(viewId, viewName, headerConfig);
        }}
        onDeleteView={handleDeleteView}
        onMakeDefaultView={handleMakeDefaultView}
      />
    ),
    [
      activeView,
      baseDefaultHeaders,
      viewAccess,
      customSavedViews,
      defaultView,
      effectiveViewId,
      handleCreateView,
      handleDeleteView,
      handleEditDefaultView,
      handleMakeDefaultView,
      handleSelectDefaultView,
      handleSelectView,
      persistedDefaultViewId,
      updateView,
    ],
  );

  return (
    <React.Fragment>
      {isLoading ? (
        <Loader info="Loading..." />
      ) : (
        <Mui.Box
          sx={{
            ...companyUi.page,
            px: 2,
            mt: 1,
            pb: 2,
          }}
        >
          <Mui.Box ref={customFilterRef} sx={companyUi.stickyCustomFilterBar}>
            <CustomFilter
              title="Companies"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={allCompanies}
              filteredData={visibleCompanies}
              filteredDataReady={filteredDataReady}
              onFilteredDataChange={handleFilteredDataChange}
              // Was hardcoded true (any signed-in user, even one with no
              // access at all) -- download/sort/filter/default-view are
              // view-only actions, so gate on real viewer access instead,
              // matching TeamZone Reports' pattern.
              screenAccess={viewAccess}
              hideCount={true}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={reportMetadata}
              labeledToolbar={true}
              searchPlaceholder="Search..."
            />
          </Mui.Box>
          <Mui.Box sx={{ ...companyUi.sectionCard, mt: 1.5 }}>
            <Mui.TableContainer
              sx={{
                width: "100%",
                overflowX: "visible",
                pb: footerHeight ? `${footerHeight}px` : 0,
              }}
              ref={tableContainerRef}
            >
              <ApproveTable stickyHeader sx={{ width: "100%", tableLayout: "fixed" }}>
                <Mui.TableHead sx={designShell.tableStickyHead}>
                  <Mui.TableRow
                    maxWidth="xl"
                    align="left"
                    sx={designShell.tableStickyHeadRow}
                  >
                    {visibleHeaders?.map?.((value, headerIndex) => (
                      <Mui.TableCell
                        key={value.key || "action"}
                        sx={{
                          ...companyUi.tableHeadCell,
                          top: APP_BAR_OFFSET + customFilterHeight,
                          ...(headerIndex === 0 ? { borderTopLeftRadius: 0 } : {}),
                          ...(headerIndex === visibleHeaders.length - 1
                            ? { borderTopRightRadius: 0 }
                            : {}),
                        }}
                      >
                        {value?.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {visibleCompanies?.length === 0 ? (
                    <TableEmptyState
                      dataLength={masterDataForSearch}
                      colSpan={visibleHeaders.length}
                    />
                  ) : (
                    visibleCompanies
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      ?.map((data) => (
                        <Mui.TableRow
                          key={data?.id}
                          hover
                          onClick={() => onRowClick?.(data)}
                          sx={{
                            cursor: onRowClick ? "pointer" : "default",
                            ...designShell.tableRowHover,
                            ...(data?.id === selectedCompanyId
                              ? companyUi.selectedRow
                              : {}),
                            "&:last-child td": { borderBottom: "none" },
                          }}
                        >
                          <>
                            {visibleHeaders.map((head) =>
                              head.key === "companyname" ? (
                                <Mui.TableCell
                                  key={head.key}
                                  align="left"
                                  sx={companyUi.tableBodyCell}
                                >
                                  <Mui.Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ minWidth: 0 }}
                                  >
                                    {data?.company_logo_url ? (
                                      <Mui.Box
                                        sx={{
                                          width: 54,
                                          height: 34,
                                          flexShrink: 0,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <img
                                          src={data.company_logo_url}
                                          alt={data?.companyname || "Company Logo"}
                                          style={{
                                            display: "block",
                                            width: "auto",
                                            height: "auto",
                                            maxWidth: "100%",
                                            maxHeight: "100%",
                                            objectFit: "contain",
                                            objectPosition: "center",
                                          }}
                                        />
                                      </Mui.Box>
                                    ) : (
                                      <Mui.Avatar
                                        sx={{ width: 28, height: 28, flexShrink: 0 }}
                                        alt={data?.companyname
                                          ?.charAt(0)
                                          ?.toUpperCase()}
                                      />
                                    )}
                                    <Mui.Tooltip
                                      arrow
                                      slotProps={tooltipStyleProps}
                                      title={data?.companyname}
                                    >
                                      <Mui.Typography
                                        sx={{
                                          fontWeight:
                                            data?.id === selectedCompanyId
                                              ? 700
                                              : 500,
                                          color:
                                            data?.id === selectedCompanyId
                                              ? designColors.slate800
                                              : "inherit",
                                          fontSize: 14,
                                          minWidth: 0,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {data?.companyname || "N/A"}
                                      </Mui.Typography>
                                    </Mui.Tooltip>
                                  </Mui.Stack>
                                </Mui.TableCell>
                              ) : head.key === "status" ? (
                                <Mui.TableCell
                                  key={head.key}
                                  align="left"
                                  sx={companyUi.tableBodyCell}
                                >
                                  {statusChip(data?.status || "Inactive")}
                                </Mui.TableCell>
                              ) : head.key === "address" ? (
                                <Mui.TableCell
                                  key={head.key}
                                  align="left"
                                  sx={companyUi.tableBodyCell}
                                >
                                  <Mui.Tooltip
                                    arrow
                                    slotProps={tooltipStyleProps}
                                    title={data?.address}
                                  >
                                    <Mui.Typography
                                      sx={{
                                        textOverflow: "ellipsis",
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        maxWidth: 200,
                                        fontSize: 14,
                                      }}
                                    >
                                      {data?.address || "N/A"}
                                    </Mui.Typography>
                                  </Mui.Tooltip>
                                </Mui.TableCell>
                              ) : (
                                <Mui.TableCell
                                  key={head.key}
                                  sx={companyUi.tableBodyCell}
                                >
                                  <Mui.Tooltip
                                    arrow
                                    slotProps={tooltipStyleProps}
                                    title={data[head.key] || ""}
                                  >
                                    <Mui.Box
                                      component="span"
                                      sx={{
                                        display: "block",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {data[head.key] || "N/A"}
                                    </Mui.Box>
                                  </Mui.Tooltip>
                                </Mui.TableCell>
                              ),
                            )}
                          </>
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </ApproveTable>
            </Mui.TableContainer>
            <Mui.Box ref={footerRef} sx={companyUi.stickyFooterBar}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: visibleCompanies.length,
                itemLabel: "companies",
                onPageChange: setPage,
                onRowsPerPageChange: handleChangeRowsPerPage,
              })}
            </Mui.Box>
          </Mui.Box>
        </Mui.Box>
      )}
      {/* The row-level "more actions" kebab was removed from the list view
          (same actions are already reachable via "More Actions" in the
          company's detail panel), but this Menu still needs to exist
          somewhere so that panel button has something to open — it acts on
          editDetails (the shared "currently selected company" state) rather
          than any specific row, so a single instance here covers every
          trigger. */}
      <Mui.Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        elevation={0}
        PaperProps={{
          sx: designShell.actionMenuPaper,
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
      >
        {editDetails?.isactive &&
        editDetails?.isprimary === false &&
        hasApproverAccess ? (
          <Mui.MenuItem
            sx={{
              ...designShell.actionMenuItem,
              width: "100%",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(null);
              changeCompanyType();
            }}
          >
            Primary
          </Mui.MenuItem>
        ) : null}
        {!hasApproverAccess ? null : editDetails?.isactive &&
          !editDetails?.isprimary ? (
          <Mui.MenuItem
            sx={{
              ...designShell.actionMenuItem,
              width: "100%",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(null);
              activatedAndDeactivateCompany("Inactivate", editDetails);
            }}
          >
            Inactivate
          </Mui.MenuItem>
        ) : editDetails?.isprimary ? null : (
          <Mui.MenuItem
            sx={{
              ...designShell.actionMenuItem,
              width: "100%",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(null);
              activatedAndDeactivateCompany("Activate", editDetails);
            }}
          >
            Activate
          </Mui.MenuItem>
        )}
      </Mui.Menu>
    </React.Fragment>
  );
};
